import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import FeatherIcon from "feather-icons-react";
import useAxios from "../../../utils/useAxios";
import { Link, useParams } from "react-router-dom";
import SelectInput from "../../../components/SelectInput";
import ErrorMessage from "../../../components/ErrorMessage";
import useSubmitLock from "../../../hooks/useSubmitLock";
import TournamentBlockToggle from "../TournamentBlockToggle";
import { Block, formatLastName, getTeamName } from "../../../constants";
import { normalizeTeamPlayers } from "../../../utils/normalizeTeamPlayers";
import PageLoader from "../../../components/PageLoader";

const ratingModeOptions = [
    { id: 1, name: "Manual" },
    { id: 2, name: "Auto" },
    { id: 3, name: "Auto (fixed points)" },
];

// Site-wide base curve for "Auto (fixed points)" - one fixed 64-place
// table for every tournament (not per-tournament editable, and not just a
// 9-value table with a flat fallback below it - an explicit entry for
// every place up to the largest realistic bracket size). Places 1-9 are
// the original hand-picked values; 10-64 are the same 1-point floor those
// already tapered to today, made explicit rather than implicit, since a
// smooth per-place decay would only produce a couple more distinct
// integer values before rounding to 1 anyway (place 9 is already 2).
// Admins tune the *overall* scale via a single
// rating_fixed_points_coefficient multiplier (see handleSave/the input
// below) instead of editing all 64 numbers individually.
const BASE_FIXED_POINTS_MAX_PLACE = 64;
const BASE_FIXED_POINTS = Object.fromEntries(
    Array.from({ length: BASE_FIXED_POINTS_MAX_PLACE }, (_, i) => i + 1).map(place => {
        const legacy = { 1: 30, 2: 20, 3: 15, 4: 10, 5: 8, 6: 6, 7: 4, 8: 4, 9: 2 };
        return [place, legacy[place] ?? 1];
    })
);
const basePointsForPlace = (place) => BASE_FIXED_POINTS[place] ?? 1;

// Matches the site's own label style (index.css `label` rule) instead of
// the browser's default bold column heading - this is a row header, not
// emphasis.
const ratingRowHeaderStyle = {
    fontWeight: 400,
    textTransform: "uppercase",
    fontSize: "80%",
    color: "var(--secondary-color)",
};

const TournamentRatingSettings = ({ tournamentId, tournamentBlocks, setTournament: setGlobalTournament, onSaved, onClose, onReset }) => {
    const { t } = useTranslation();
    const api = useAxios();
    const { id: routeId } = useParams();
    const id = tournamentId || routeId;
    const { isSubmitting, wrapSubmit } = useSubmitLock();

    const [tournament, setTournament] = useState(null);
    const [ratings, setRatings] = useState([]);
    const [ratingBase, setRatingBase] = useState(null);
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [ratingPlayers, setRatingPlayers] = useState([]);
    const [ratingPlayersLoading, setRatingPlayersLoading] = useState(false);
    const [draftScores, setDraftScores] = useState({});
    // Guards fetchData against out-of-order responses: the mount effect
    // below fires one on load, and handleReset fires another right after
    // resetting - without this, a slower pre-reset response can land after
    // the reset's and silently overwrite the freshly-reset state with the
    // old one.
    const dataReqRef = useRef(0);

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchData = async () => {
        setLoading(true);
        dataReqRef.current += 1;
        const currentReq = dataReqRef.current;
        try {
            const tournamentRes = await api.get(`/api/v1.0/tournaments/${id}/`);
            if (dataReqRef.current !== currentReq) return;
            const data = tournamentRes.data;
            setTournament(data);
            setRatingBase(data.rating || null);

            // Independent of each other (one keys off data.club, the other
            // off data.rating) - used to run one after the other, which on
            // top of the tournament fetch itself meant three round trips in
            // a row before the Points-by-place table's own fetch (gated on
            // `settings` below) could even start. On the real prod backend
            // that added up to several seconds of the rating-base dropdown
            // and the table both sitting there looking wrong/empty rather
            // than loading - easy to mistake for actually broken. Firing
            // both in parallel cuts that wait roughly in half.
            const [ratingsResult, settingsResult] = await Promise.allSettled([
                data.club
                    ? api.get(`/api/v1.0/ratings/?club=${data.club}&limit=100`)
                    : Promise.resolve(null),
                data.rating
                    ? api.get(`/api/v1.0/tournaments/${id}/rating-settings/`)
                    : Promise.resolve(null),
            ]);
            if (dataReqRef.current !== currentReq) return;

            if (ratingsResult.status === "fulfilled" && ratingsResult.value) {
                const ratingsRes = ratingsResult.value;
                setRatings(
                    (ratingsRes.data.results || ratingsRes.data || []).map(r => ({
                        id: r.id,
                        name: r.name,
                        // "Auto (fixed points)" scale multiplier - lives on
                        // the Rating itself (edited in RatingForm.js, shared
                        // by every tournament linked to it), not here, so
                        // it only needs to come along for handleSave's mode
                        // 3 branch to read.
                        fixed_points_coefficient: r.fixed_points_coefficient,
                    }))
                );
            } else if (ratingsResult.status === "rejected") {
                console.error(ratingsResult.reason);
            }

            if (data.rating) {
                if (settingsResult.status === "fulfilled" && settingsResult.value) {
                    setSettings(settingsResult.value.data);
                } else if (settingsResult.status === "rejected") {
                    // A tournament can have a rating base linked (data.rating)
                    // with no rating-settings row created for it yet - the
                    // backend only creates one once something is actually
                    // saved. Left as `null` here, `settings?.rating_mode`
                    // never equals 1, so the Points-by-place effect below
                    // never fires and the table just looks broken/empty until
                    // the admin hits Save once (which PATCHes rating-settings
                    // with the {rating_mode:1} fallback and creates the row).
                    // Defaulting locally to the same {rating_mode:1} the mode
                    // dropdown already falls back to visually
                    // (default_value={settings?.rating_mode || 1} below) lets
                    // the table load immediately on first open instead.
                    if (settingsResult.reason?.response?.status === 404) {
                        setSettings({ rating_mode: 1 });
                    } else {
                        console.error(settingsResult.reason);
                    }
                }
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    // Manual mode's place -> participant -> points table (moved here from
    // the participants "Rating" tab, TournamentRatingPlayers.js, so a
    // manual score gets set once per place instead of hunting through that
    // tab's own participant cards). Auto/Auto (fixed points) recompute and
    // overwrite every score on the next Save anyway, so this table only
    // ever needs to exist - and matters - in mode 1.
    const placeSortValue = (item) => {
        const raw = item?.tournament_place;
        if (!raw || raw === 0 || raw === "0" || raw === "-") return Infinity;
        const first = parseInt(String(raw).split("-")[0], 10);
        return isNaN(first) ? Infinity : first;
    };

    const getPlace = (item) =>
        item?.tournament_place && item.tournament_place !== 0 && item.tournament_place !== "0"
            ? item.tournament_place
            : t("-");

    // "И. Фамилия" abbreviated form, same as the groups table
    // (TournamentGroupTable.js) - one line per participant (doubles gets
    // one line per player), not the full-name form used elsewhere in this
    // file's ecosystem.
    const renderPlayerLine = (p, key) => (
        <div key={key} className="black-name">
            {p?.first_name && <>{p.first_name[0]}.&nbsp;</>}
            {formatLastName(p?.last_name)}
        </div>
    );

    const renderParticipantName = (item) => {
        const grade = tournament?.grade || 1;
        if (grade === 2) {
            // Two lines max, one per slot - a pair carrying a third member
            // (duplicate `sorting`, see src/utils/normalizeTeamPlayers.js)
            // rendered three names in this table.
            return normalizeTeamPlayers(item.players, 2).map((p, i) => renderPlayerLine(p, i));
        }
        if (grade === 3) {
            return <div className="black-name">{item.name || getTeamName(item, t)}</div>;
        }
        return renderPlayerLine(item);
    };

    const fetchRatingPlayers = async () => {
        setRatingPlayersLoading(true);
        try {
            const grade = tournament?.grade || 1;
            const res = await api.get(`/api/v1.0/tournaments/${id}/players-rating/?limit=100&grade=${grade}`);
            const results = (res.data.results || res.data || []).slice().sort((a, b) => placeSortValue(a) - placeSortValue(b));
            setRatingPlayers(results);
            setDraftScores({});
        } catch (e) {
            console.error(e);
        } finally {
            setRatingPlayersLoading(false);
        }
    };

    useEffect(() => {
        if (settings?.rating_mode === 1 && ratingBase && tournament?.grade != null) {
            fetchRatingPlayers();
        } else {
            setRatingPlayers([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settings?.rating_mode, ratingBase, tournament?.grade, id]);

    const handleScoreBlur = async (participantId, score) => {
        const grade = tournament?.grade || 1;
        const parsed = parseInt(score, 10) || 0;
        try {
            await api.patch(`/api/v1.0/tournaments/${id}/players-rating/?grade=${grade}`, {
                id: participantId,
                score: parsed
            });
            setRatingPlayers(prev => prev.map(item => {
                const itemId = grade > 1 ? item.id : item.player_id;
                return itemId === participantId ? { ...item, tournament_score: parsed } : item;
            }));
            setDraftScores(prev => {
                const next = { ...prev };
                delete next[participantId];
                return next;
            });
        } catch (e) {
            console.error(e);
        }
    };

    const handleChange = (field) => (eOrValue) => {
        const value = eOrValue?.target ? eOrValue.target.value : eOrValue;
        setSettings(prev => ({
            ...prev,
            [field]: value,
        }));
        setErrors(prev => ({
            ...prev,
            [field]: undefined
        }));
    };

    const handleSave = wrapSubmit(async () => {
        setErrors({});
        setLoading(true);
        try {
            if (ratingBase !== (tournament.rating || null)) {
                await api.patch(`/api/v1.0/tournaments/${id}/`, {
                    rating: ratingBase === null ? "" : ratingBase
                });
            }

            if (!ratingBase) {
                onSaved?.();
                return;
            }

            // Defensive fallback, matching the dropdown's own seeding above -
            // PATCHing this endpoint with a null body gets rejected by the
            // backend, silently aborting the save before onSaved() (and the
            // tournament refetch it triggers) ever runs.
            await api.patch(`/api/v1.0/tournaments/${id}/rating-settings/`, settings || { rating_mode: 1 });

            const mode = parseInt(settings.rating_mode, 10);
            if (mode === 2 || mode === 3) {
                const grade = tournament?.grade || 1;
                const res = await api.get(`/api/v1.0/tournaments/${id}/players-rating/?limit=100&grade=${grade}`);
                const players = res.data.results || res.data || [];

                let maxPlayoffPlace = 0;
                players.forEach(p => {
                    if (p.tournament_place && p.tournament_place !== 0 && p.tournament_place !== "0" && p.tournament_place !== "-") {
                        const parts = String(p.tournament_place).split("-");
                        const endPlace = parseInt(parts[parts.length - 1], 10);
                        if (!isNaN(endPlace) && endPlace > maxPlayoffPlace) {
                            maxPlayoffPlace = endPlace;
                        }
                    }
                });

                const noPlayoffPlayers = players.filter(p => !p.tournament_place || p.tournament_place === 0 || p.tournament_place === "0" || p.tournament_place === "-");

                if (noPlayoffPlayers.length > 0) {
                    const playersByGroup = {};
                    noPlayoffPlayers.forEach(p => {
                        const gid = p.group_id || 'no_group';
                        if (!playersByGroup[gid]) playersByGroup[gid] = [];
                        playersByGroup[gid].push(p);
                    });

                    Object.values(playersByGroup).forEach(groupPlayers => {
                        groupPlayers.sort((a, b) => {
                            if (a.group_place && b.group_place) return a.group_place - b.group_place;
                            if (a.group_place) return -1;
                            if (b.group_place) return 1;

                            if (b.tournament_wins !== a.tournament_wins) return b.tournament_wins - a.tournament_wins;
                            const diffA = a.tournament_difference || 0;
                            const diffB = b.tournament_difference || 0;
                            if (diffB !== diffA) return diffB - diffA;
                            return a.tournament_losses - b.tournament_losses;
                        });

                        let currentPlace = maxPlayoffPlace + 1;
                        let previousWins = -1;
                        let previousLosses = -1;
                        let previousDiff = null;

                        groupPlayers.forEach((p, index) => {
                            if (p.group_place) {
                                p.virtual_place = p.group_place;
                            } else {
                                const pDiff = p.tournament_difference || 0;
                                if (p.tournament_wins !== previousWins || p.tournament_losses !== previousLosses || pDiff !== previousDiff) {
                                    currentPlace = maxPlayoffPlace + index + 1;
                                    previousWins = p.tournament_wins;
                                    previousLosses = p.tournament_losses;
                                    previousDiff = pDiff;
                                }
                                p.virtual_place = currentPlace;
                            }
                        });
                    });
                }

                const base = parseFloat(settings.rating_base_points) || 100;
                const tcoef = parseFloat(settings.rating_tournament_coef) || 1.0;
                const dcoef = parseFloat(settings.rating_decay_coef) || 0.5;
                // Lives on the linked Rating itself (RatingForm.js), not this
                // tournament's own settings - shared by every tournament
                // linked to that rating instead of being set per-tournament.
                const fixedCoef = parseFloat(ratings.find(r => r.id === ratingBase)?.fixed_points_coefficient) || 1;

                for (const item of players) {
                    let place = 0;

                    if (item.virtual_place) {
                        place = item.virtual_place;
                    } else {
                        const placeInfo = item.tournament_place;
                        if (!placeInfo || placeInfo === 0 || placeInfo === "0" || placeInfo === "-") continue;
                        place = parseInt(String(placeInfo).split("-")[0], 10);
                        if (isNaN(place) || place <= 0) continue;
                    }

                    let score = 0;
                    if (mode === 2) {
                        // Tweak the formula to have a slightly sharper drop-off for lower places
                        // This ensures matches for 9th/10th place have a more logical point difference
                        const progressiveDecay = dcoef + (place * 0.02);
                        score = Math.round((base * tcoef) / Math.pow(place, progressiveDecay));
                    } else if (mode === 3) {
                        // Fixed site-wide base curve (BASE_FIXED_POINTS above),
                        // scaled by the linked Rating's own coefficient
                        // (fixedCoef, computed above the loop) - shared by
                        // every tournament linked to that rating, not set
                        // per-tournament.
                        score = Math.round(basePointsForPlace(place) * fixedCoef);
                    }

                    const participantId = grade > 1 ? item.id : item.player_id;

                    if (item.tournament_score !== score) {
                        await api.patch(`/api/v1.0/tournaments/${id}/players-rating/?grade=${grade}`, {
                            id: participantId,
                            score: score
                        });
                    }
                }
            }

            onSaved?.();
        } catch (e) {
            if (e.response?.status === 400) {
                setErrors(e.response.data);
            } else {
                console.error(e);
            }
        } finally {
            setLoading(false);
        }
    });

    const handleReset = wrapSubmit(async () => {
        if (!window.confirm(t("Are you sure you want to reset?"))) return;
        setLoading(true);

        try {
            await api.delete(`/api/v1.0/tournaments/${id}/rating-settings/`);
            // Re-fetch the authoritative post-reset state instead of just
            // assuming it's null locally.
            await fetchData();
            onReset ? onReset() : onSaved?.();
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    });

    if (loading && !tournament) {
        return <PageLoader />;
    }

    if (!tournament) {
        return <PageLoader />;
    }

    return (
        <form className="modal-form">
            <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                <TournamentBlockToggle
                    tournamentId={id}
                    tournamentBlocks={tournamentBlocks}
                    setTournament={setGlobalTournament}
                    blockType={Block.RANKING}
                />
            </div>

            <SelectInput
                data={[
                    { id: null, name: t("No rating") },
                    ...ratings
                ]}
                label_text={t("Rating base")}
                default_text={t("No rating")}
                input_name="rating"
                default_value={ratingBase}
                setInputValue={(id) => {
                    setRatingBase(id);
                    // Linking a rating base for the first time (tournament
                    // had none at page load, so fetchData's own rating-
                    // settings fetch never ran) left `settings` null - the
                    // Rating mode dropdown below still visually defaulted to
                    // "Manual" (default_value={settings?.rating_mode || 1}),
                    // but that's cosmetic only. Saving without ever touching
                    // that dropdown then PATCHed rating-settings/ with a
                    // null body, which the backend rejected - silently
                    // failing before onSaved() (and the tournament refetch
                    // it triggers) ever ran, so the newly-linked rating
                    // looked unset everywhere until a manual page reload.
                    if (id && !settings) {
                        setSettings({ rating_mode: 1 });
                    }
                }}
            />

            {ratingBase ? (
                <>
                    <div>
                        <SelectInput
                            default_text={t("Rating mode")}
                            label_text={t("Rating mode")}
                            data={ratingModeOptions}
                            default_value={settings?.rating_mode || 1}
                            setInputValue={handleChange("rating_mode")}
                        />
                        {errors.rating_mode && (
                            <div style={{marginTop: "-15px"}}>
                                <ErrorMessage error={errors.rating_mode} />
                            </div>
                        )}
                    </div>
                    {settings?.rating_mode === 1 && (
                        <div style={{ marginTop: "10px" }}>
                            <label>{t("Points by place")}</label>
                            {ratingPlayersLoading ? (
                                <div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "10px 0", color: "var(--secondary-color)" }}>
                                    <span className="loading-dots">
                                        <span></span><span></span><span></span>
                                    </span>
                                    {t("Loading...")}
                                </div>
                            ) : ratingPlayers.length === 0 ? (
                                <p style={{ color: "var(--muted)" }}>{t("Participants")}: 0</p>
                            ) : (
                                // Same rounded/bordered box the rest of the settings modal
                                // uses for a repeated-item list (.group-stage, see
                                // TournamentDrawStagesSettings.js/TournamentScheduleSettings.js) -
                                // not a plain HTML table, to look like the rest of this modal.
                                <div className="group-stage">
                                    <div style={{ display: "flex", alignItems: "center", padding: "8px 0", ...ratingRowHeaderStyle }}>
                                        <span style={{ width: "50px" }}>{t("Place")}</span>
                                        <span style={{ flex: 1 }}>{t("Participant")}</span>
                                        <span>{t("Points")}</span>
                                    </div>
                                    {ratingPlayers.map((item, index) => {
                                        const participantId = (tournament?.grade || 1) > 1 ? item.id : item.player_id;
                                        const currentScore = draftScores[participantId] ?? item.tournament_score ?? 0;
                                        return (
                                            <div
                                                key={participantId ?? index}
                                                style={{ display: "flex", alignItems: "center", padding: "8px 0", borderTop: "1px solid var(--white-blue)" }}
                                            >
                                                <span style={{ width: "50px", color: "var(--secondary-color)" }}>{getPlace(item)}</span>
                                                <span style={{ flex: 1 }}>{renderParticipantName(item)}</span>
                                                <input
                                                    type="number"
                                                    className="rating-input"
                                                    value={currentScore}
                                                    style={{ width: "70px", textAlign: "right", boxSizing: "border-box", display: "inline-block", margin: 0 }}
                                                    onChange={(e) => setDraftScores(prev => ({ ...prev, [participantId]: e.target.value }))}
                                                    onBlur={() => handleScoreBlur(participantId, currentScore)}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}
                    {settings?.rating_mode === 2 &&
                        <>
                            <div>
                                <label>{t("Base points")}</label>
                                <input
                                    type="number"
                                    value={settings.rating_base_points || ""}
                                    onChange={handleChange("rating_base_points")}
                                />
                                {errors.rating_base_points && (
                                    <ErrorMessage error={errors.rating_base_points} />
                                )}
                            </div>
                            <div>
                                <label>{t("Tournament coefficient")}</label>
                                <input
                                    type="number"
                                    step="0.1"
                                    value={settings.rating_tournament_coef || ""}
                                    onChange={handleChange("rating_tournament_coef")}
                                />
                                {errors.rating_tournament_coef && <ErrorMessage error={errors.rating_tournament_coef[0]} />}
                            </div>
                            <div>
                                <label>{t("Decay coefficient")}</label>
                                <input
                                    type="number"
                                    step="0.1"
                                    value={settings.rating_decay_coef || ""}
                                    onChange={handleChange("rating_decay_coef")}
                                />
                                {errors.rating_decay_coef && <ErrorMessage error={errors.rating_decay_coef[0]} />}
                            </div>
                            <br/>
                        </>
                    }
                    {settings?.rating_mode === 3 && (
                        <div>
                            <label>{t("Points coefficient")}</label>
                            {/* Not edited here - it lives on the linked Rating
                                itself (RatingForm.js) so every tournament sharing
                                that rating uses the same coefficient against the
                                same site-wide base curve (BASE_FIXED_POINTS),
                                instead of each tournament needing its own. */}
                            <p style={{ color: "var(--muted)" }}>
                                {t("Set once on the rating base itself - shared by every tournament linked to it")}
                                {ratingBase && (
                                    <>
                                        {" "}
                                        <Link to={`/admin/ratings/${ratingBase}`}>{t("Edit rating")}</Link>
                                    </>
                                )}
                            </p>
                        </div>
                    )}
                </>
            ) : (
                <p style={{ color: "var(--muted)" }}>{t("Link a rating base to configure scoring")}</p>
            )}

            <ul className="tabs settings-form-actions">
                <li>
                    <a
                        href="#"
                        className={`action-button ${loading || isSubmitting ? "disabled" : ""}`}
                        onClick={loading || isSubmitting ? (e) => e.preventDefault() : handleSave}
                        aria-disabled={loading || isSubmitting}
                    >
                        <FeatherIcon icon="check"/> {loading ? t("Saving...") : t("Save")}
                    </a>
                </li>
                {tournament.rating && (
                    <li>
                        <a
                            href="#"
                            className={`action-button ${loading || isSubmitting ? "disabled" : ""}`}
                            onClick={loading || isSubmitting ? (e) => e.preventDefault() : handleReset}
                            aria-disabled={loading || isSubmitting}
                        >
                            <FeatherIcon icon="rotate-ccw"/> {loading ? t("Resetting...") : t("Reset")}
                        </a>
                    </li>
                )}
            </ul>
        </form>
    );
};

export default TournamentRatingSettings;
